/**
 * Booking preferences: showing length and how a prospect's picked time is handled. Saved through the
 * listing update (defaultShowingDurationMinutes, prospectBookingMode). On the live listing it has its
 * own "Save settings" button; inside the guide (`embedded`) it saves with "Save and continue" through
 * its handle, like the guide's other steps.
 */

import { useEffect, useImperativeHandle, useState, type Ref } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../../components/ui/button';
import { updateListing, type Listing } from '../../../../lib/leasing.api';
import type { GuideStepHandle } from '../../listing-guide/guide-steps';
import { FIELD_CLASS, ShowingsCard, usePanelReport, type PanelReport } from './panel-support';

type BookingMode = Listing['prospectBookingMode'];

const DURATION_CHOICES = [15, 30, 45, 60, 90, 120];

/** The usual lengths, plus the saved one when it is something else (so it never silently changes). */
function durationOptions(current: number): number[] {
  return DURATION_CHOICES.includes(current) ? DURATION_CHOICES : [...DURATION_CHOICES, current].sort((a, b) => a - b);
}

const BOOKING_MODES: { value: BookingMode; label: string }[] = [
  { value: 'proposal', label: 'I confirm each request first' },
  { value: 'direct', label: 'It books right away' },
];

interface BookingPreferencesProps {
  listing: Listing;
  isClosed: boolean;
  embedded: boolean;
  handleRef: Ref<GuideStepHandle>;
  report: PanelReport;
}

interface Saved {
  durationMinutes: number;
  bookingMode: BookingMode;
}

export function BookingPreferences({ listing, isClosed, embedded, handleRef, report }: BookingPreferencesProps) {
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState<Saved>({
    durationMinutes: listing.defaultShowingDurationMinutes,
    bookingMode: listing.prospectBookingMode,
  });
  const [durationMinutes, setDurationMinutes] = useState(listing.defaultShowingDurationMinutes);
  const [bookingMode, setBookingMode] = useState<BookingMode>(listing.prospectBookingMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);

  // Follow the listing when it changes elsewhere (after its own refetch).
  useEffect(() => {
    setSaved({ durationMinutes: listing.defaultShowingDurationMinutes, bookingMode: listing.prospectBookingMode });
    setDurationMinutes(listing.defaultShowingDurationMinutes);
    setBookingMode(listing.prospectBookingMode);
  }, [listing.defaultShowingDurationMinutes, listing.prospectBookingMode]);

  const dirty = !isClosed && (durationMinutes !== saved.durationMinutes || bookingMode !== saved.bookingMode);
  usePanelReport(report, saving, false, dirty);

  const save = async (): Promise<boolean> => {
    if (!dirty) return true;
    setSaving(true);
    setError(null);
    setJustSaved(false);
    try {
      await updateListing(listing.id, {
        defaultShowingDurationMinutes: durationMinutes,
        prospectBookingMode: bookingMode,
      });
      setSaved({ durationMinutes, bookingMode });
      void queryClient.invalidateQueries({ queryKey: ['listing', listing.id] });
      setJustSaved(true);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
      return false;
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(handleRef, () => ({ save, isDirty: () => dirty }));

  return (
    <ShowingsCard data-testid="showing-settings-panel" title="Booking preferences">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="showing-duration-input" className="mb-1 block text-sm font-medium text-foreground">
            Showing length
          </label>
          <select
            id="showing-duration-input"
            data-testid="showing-duration-input"
            value={durationMinutes}
            onChange={(e) => {
              setDurationMinutes(Number(e.target.value));
              setJustSaved(false);
            }}
            disabled={isClosed || saving}
            className={FIELD_CLASS}
          >
            {durationOptions(durationMinutes).map((minutes) => (
              <option key={minutes} value={minutes}>
                {minutes} minutes
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="showing-booking-mode" className="mb-1 block text-sm font-medium text-foreground">
            When someone picks a time
          </label>
          <select
            id="showing-booking-mode"
            data-testid="showing-booking-mode"
            value={bookingMode}
            onChange={(e) => {
              setBookingMode(e.target.value as BookingMode);
              setJustSaved(false);
            }}
            disabled={isClosed || saving}
            className={FIELD_CLASS}
          >
            {BOOKING_MODES.map((mode) => (
              <option key={mode.value} value={mode.value}>
                {mode.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!embedded && !isClosed && (
        <div className="mt-4 flex items-center gap-3">
          <Button type="button" data-testid="showing-settings-save-btn" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Save Settings'}
          </Button>
          {justSaved && <span className="text-sm font-medium text-success">Settings saved.</span>}
        </div>
      )}
      {embedded && dirty && (
        <p className="mt-3 text-xs text-muted-foreground">Saved when you continue.</p>
      )}
      {error && (
        <p role="alert" data-testid="showing-settings-error" className="mt-3 text-sm text-destructive">
          {error}
        </p>
      )}
      {isClosed && (
        <p className="mt-3 text-xs italic text-muted-foreground">Settings are read-only for closed listings.</p>
      )}
    </ShowingsCard>
  );
}
